import type { PropertySchema } from '@takeshape/schema';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import type { ImmutableMap, MaybeImmutableObject } from '../../common/utils/immutable-types';
import type { ResolveRef } from '../types';

/**
 * Get the property name used as the discriminator
 * @param unionSchema a discriminated union schema with discriminator and oneOf properties
 */
export function getDiscriminatorName(unionSchema: ImmutableMap<PropertySchema>): string | undefined {
  return unionSchema.getIn(['discriminator', 'propertyName']);
}

/**
 * Given a union schema and object data find the discriminator value
 * @param unionSchema
 * @param data
 */
export function getDiscriminatorValue(
  unionSchema: ImmutableMap<PropertySchema>,
  data?: MaybeImmutableObject
): string | undefined {
  const name = getDiscriminatorName(unionSchema);
  return name && data && (get(data, name) as string | undefined);
}

/**
 * Given an object schema and discriminator name determine it's constant discriminator value
 * @param objectSchema an object schema that is part of a discriminated union
 * @param discriminatorName the name of the property used to discriminate the union
 */
export function getDiscriminatorConstant(
  objectSchema: ImmutableMap<PropertySchema>,
  discriminatorName: string
): string | undefined {
  const discriminatorSchema = objectSchema.getIn<ImmutableMap<PropertySchema>>(['properties', discriminatorName]);
  return discriminatorSchema?.getIn(['enum', 0]) ?? discriminatorSchema?.get('const');
}

function isImmutableMap(data: unknown): data is ImmutableMap<Record<string, unknown>> {
  return Map.isMap(data);
}

function get(data: MaybeImmutableObject, name: string): unknown {
  return isImmutableMap(data) ? data.get(name) : data[name];
}

function keys(data: MaybeImmutableObject): string[] {
  return isImmutableMap(data) ? data.keySeq().toArray() : Object.keys(data);
}

/**
 * Narrow a discriminated union schema given the discriminator value.
 * Returns undefined when a schema cannot be found with a matching discriminator value
 * @param resolveRef provided by field props
 * @param unionSchema a discriminated union schema with discriminator and oneOf properties
 * @param data object data may be undefined for convenience
 */
export function findMatchingSchema(
  resolveRef: ResolveRef,
  unionSchema: ImmutableMap<PropertySchema>,
  data?: MaybeImmutableObject | string
): ImmutableMap<PropertySchema> | undefined {
  if (!data) {
    return;
  }

  const oneOf = unionSchema.get('oneOf');
  const discriminatorName = getDiscriminatorName(unionSchema);
  if (!oneOf || typeof discriminatorName !== 'string') {
    return;
  }

  const discriminatorValue = typeof data === 'string' ? data : get(data, discriminatorName);
  return oneOf.find((itemSchema) => {
    return getDiscriminatorConstant(resolveRef(itemSchema!), discriminatorName) === discriminatorValue;
  });
}

const importantFields = ['type', 'allOf', 'oneOf', 'anyOf', 'items', 'enum', 'const', '$ref'];
function isSchemaEqual(a?: PropertySchema, b?: PropertySchema) {
  if (!a || !b) {
    return false;
  }

  return isEqual(pick(a, importantFields), pick(b, importantFields));
}

function findMatchingObjectSchema(
  resolveRef: ResolveRef,
  unionSchema: ImmutableMap<PropertySchema>,
  data?: MaybeImmutableObject
) {
  const matchingSchema = findMatchingSchema(resolveRef, unionSchema, data);
  return matchingSchema && resolveRef(matchingSchema);
}

export function mergeUnionData(
  resolveRef: ResolveRef,
  unresolvedUnionSchema: ImmutableMap<PropertySchema>,
  newData: MaybeImmutableObject,
  currentData?: MaybeImmutableObject
): Record<string, unknown> {
  const unionSchema = resolveRef(unresolvedUnionSchema);
  const update: Record<string, unknown> = {
    ...(isImmutableMap(newData) ? newData.toJS() : newData)
  };
  const targetSchema = findMatchingObjectSchema(resolveRef, unionSchema, newData);
  const currentSchema = findMatchingObjectSchema(resolveRef, unionSchema, currentData);
  if (!targetSchema) {
    return update;
  }

  const targetProperties = targetSchema.get('properties')?.toJS();
  if (targetProperties) {
    const currentProperties = currentSchema?.get('properties')?.toJS() ?? {};
    const allowAdditional = targetSchema.get('additionalProperties') !== false;
    const keysToCopy =
      allowAdditional && currentData ? uniq([...keys(targetProperties), ...keys(currentData)]) : keys(targetProperties);
    for (const key of keysToCopy) {
      if (update[key] !== undefined) {
        continue;
      }

      const currentValue = currentData ? get(currentData, key) : undefined;
      const defaultValue = targetProperties[key]?.default;

      if (
        currentValue !== undefined &&
        (targetProperties[key] ? isSchemaEqual(currentProperties[key], targetProperties[key]) : allowAdditional)
      ) {
        update[key] = currentValue;
      } else if (defaultValue !== undefined) {
        update[key] = defaultValue;
      }
    }
  }

  return update;
}
